import React, {
	type ReactNode,
	createContext,
	useContext,
	useCallback,
	useRef,
	useMemo,
	useEffect,
	type MutableRefObject,
} from 'react';
import { checkIsClamped, getClosestTextElement } from './utils.tsx';

export const CELL_HOVER_DELAY = 200;

type CellHoverProps = {
	children: ReactNode;
};
type CellHoverHandlers = {
	handleMouseEnter: () => void;
	handleMouseLeave: () => void;
	onPreload: MutableRefObject<(() => void) | null>;
	onLoad: MutableRefObject<(() => void) | null>;
	onShowPopup: MutableRefObject<(() => void) | null>;
	onHidePopup: MutableRefObject<(() => void) | null>;
};

export type CellHoverCallbackProps = {
	contentRef?: MutableRefObject<HTMLElement | null>;
	onPreload?: () => void;
	onLoad?: () => void;
	onPopupToggle: (value: boolean) => void;
	shouldGetParentRef?: boolean;
};

export const CellHoverHandlerContext = createContext<CellHoverHandlers | null>(null);

const onShowPopup = ({ contentRef, onPopupToggle, shouldGetParentRef }: CellHoverCallbackProps) => {
	const elementWithTextContent = contentRef?.current
		? getClosestTextElement(
				shouldGetParentRef ? contentRef?.current.parentElement : contentRef?.current,
			)
		: undefined;

	const clientWidth = elementWithTextContent?.clientWidth || 0;
	const scrollWidth = elementWithTextContent?.scrollWidth || 0;
	const isClamped = checkIsClamped(elementWithTextContent);

	// If contentRef is not provided then the popover should always be visible on hover.
	if (!contentRef?.current || scrollWidth > clientWidth || isClamped) {
		onPopupToggle(true);
	}
};

export const useCellHoverCallbacks = ({
	contentRef,
	onPreload,
	onPopupToggle,
	onLoad,
	shouldGetParentRef,
}: CellHoverCallbackProps) => {
	const handlers = useContext(CellHoverHandlerContext);

	const onHidePopup = useCallback(() => {
		onPopupToggle(false);
	}, [onPopupToggle]);

	if (!handlers) return;

	if (handlers.onPreload) {
		handlers.onPreload.current = onPreload || null;
	}

	if (handlers.onLoad) {
		handlers.onLoad.current = onLoad || null;
	}

	handlers.onShowPopup.current = () =>
		onShowPopup({ contentRef, onPopupToggle, shouldGetParentRef });
	handlers.onHidePopup.current = onHidePopup;
};

export const CellHoverProvider = ({ children }: CellHoverProps) => {
	const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const onPreloadRef = useRef<() => void>(null);
	const onLoadRef = useRef<() => void>(null);
	const onShowPopupRef = useRef<() => void>(null);
	const onHidePopupRef = useRef<() => void>(null);

	const cleanUpTimeout = useCallback(() => {
		if (hoverTimeoutRef.current) {
			clearTimeout(hoverTimeoutRef.current);
			hoverTimeoutRef.current = null;
		}
	}, []);

	const handleMouseEnter = useCallback(() => {
		onPreloadRef.current?.();
		hoverTimeoutRef.current = setTimeout(() => {
			onLoadRef.current?.();
			onShowPopupRef.current?.();
		}, CELL_HOVER_DELAY);
	}, []);

	const handleMouseLeave = useCallback(() => {
		cleanUpTimeout();
		onHidePopupRef.current?.();
	}, [cleanUpTimeout]);

	const handers = useMemo(
		() => ({
			handleMouseEnter,
			handleMouseLeave,
			onPreload: onPreloadRef,
			onLoad: onLoadRef,
			onShowPopup: onShowPopupRef,
			onHidePopup: onHidePopupRef,
		}),
		[handleMouseEnter, handleMouseLeave],
	);

	useEffect(() => {
		return cleanUpTimeout;
	}, [cleanUpTimeout]);

	return (
		<CellHoverHandlerContext.Provider value={handers}>{children}</CellHoverHandlerContext.Provider>
	);
};

export const useCellHoverHandlers = () => {
	const handlers = useContext(CellHoverHandlerContext);

	if (handlers === null) {
		throw new Error('useCellHoverHandlers must be used within a CellHoverProvider');
	}

	return handlers;
};
